import React, { useState, useEffect, useRef, useCallback } from 'react';
import { onGameEvent, emitGameEvent } from '../../core/GameEvents';
import { WORLD_CONFIG, DRONE_TYPE_DISPLAY, hexToCss, type DroneType } from '@astra/shared';

const PALETTE = {
  DARK: '#0f1b26',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
};

interface ConstellationMapData {
  constellationName: string;
  constellationSize: number;
  playerPosition: { x: number; y: number };
  playerRotation: number;
  planets: Array<{ id: string; name: string; systemId: string; x: number; y: number; radius: number; planetType: string; orbitalDistance?: number; hasPlayerOutpost?: boolean }>;
  stations: Array<{ id: string; name: string; x: number; y: number }>;
  asteroidZones?: Array<{ id: string; x: number; y: number; radius: number; name?: string }>;
  gateways: Array<{ id: string; name: string; x: number; y: number; targetConstellationName: string }>;
  systems: Array<{ id: string; name: string; x: number; y: number; starType: string; sunRadius: number }>;
  privateStations?: Array<{ id: string; name: string; x: number; y: number; isOwned: boolean }>;
  waypoint?: { x: number; y: number; name: string } | null;
  harvestables?: Array<{ id: string; kind: 'ice_asteroid' | 'gas_pocket' | 'wreckage'; x: number; y: number; resourceType: string }>;
  resourceZones?: Array<{ id: string; x: number; y: number; radius: number; kind: string }>;
  ownedDrones?: Array<{ id: string; droneType: string; x: number; y: number; state: string; level?: number }>;
  tutorialBeacons?: Array<{ id: string; name: string; x: number; y: number; radius: number }>;
}

interface ConstellationMapOverlayProps {
  onClose: () => void;
  onZoomToUniverse: () => void;
  mapData?: ConstellationMapData | null;
}

interface MapObject {
  id: string;
  name: string;
  x: number;
  y: number;
  type: 'planet' | 'station' | 'asteroid' | 'gateway' | 'system' | 'private_station' | 'ice_asteroid' | 'gas_pocket' | 'wreckage' | 'resource_zone' | 'player' | 'sun' | 'nebula' | 'drone' | 'drop' | 'beacon' | 'hotspot' | 'settlement' | 'tutorial_beacon';
  radius?: number;
  planetType?: string;
  starType?: string;
  targetConstellationName?: string;
  isScanned?: boolean; // True if discovered via radar scan
  isOwned?: boolean; // True if this private station belongs to the player
  resourceType?: string;
  shipClassId?: string;
  ownerName?: string;
  droneType?: string; // miner | hauler | fighter (for owned drone markers)
  droneState?: string; // idle | mining | seeking | etc.
  hasPlayerOutpost?: boolean; // true if the player has an outpost on this planet
}

interface PlayerPosition {
  x: number;
  y: number;
  rotation: number;
}

const overlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: PALETTE.DARK,
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'JetBrains Mono', monospace",
  color: PALETTE.LIGHT,
  pointerEvents: 'auto',
};

const headerStyle: React.CSSProperties = {
  padding: '15px 20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${PALETTE.DIM}`,
};

const titleStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.TEAL,
  fontWeight: 600,
};

const closeButtonStyle: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: PALETTE.PANEL,
  color: PALETTE.LIGHT,
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
};

const canvasContainerStyle: React.CSSProperties = {
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
};

const instructionsStyle: React.CSSProperties = {
  padding: '10px 20px',
  textAlign: 'center',
  color: PALETTE.DIM,
  fontSize: '11px',
  borderTop: `1px solid ${PALETTE.DIM}`,
};

const tooltipStyle: React.CSSProperties = {
  position: 'absolute',
  backgroundColor: 'rgba(15, 27, 38, 0.95)',
  border: `1px solid ${PALETTE.TEAL}`,
  borderRadius: '6px',
  padding: '12px 16px',
  fontSize: '11px',
  color: PALETTE.LIGHT,
  pointerEvents: 'none',
  zIndex: 100,
  minWidth: '180px',
  maxWidth: '280px',
  boxShadow: `0 4px 20px rgba(0, 0, 0, 0.5), 0 0 15px rgba(32, 165, 166, 0.15)`,
  backdropFilter: 'blur(8px)',
};

const tooltipHeaderStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const tooltipRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '4px',
  fontSize: '11px',
};

const tooltipLabelStyle: React.CSSProperties = {
  color: PALETTE.DIM,
};

const tooltipValueStyle: React.CSSProperties = {
  color: PALETTE.LIGHT,
  fontWeight: 600,
};

export function ConstellationMapOverlay({ onClose, onZoomToUniverse, mapData }: ConstellationMapOverlayProps) {
  // Per-constellation size — drives grid, boundary, and scale calculations. Falls
  // back to the global default if mapData is missing (e.g. early mount).
  const CONSTELLATION_SIZE = mapData?.constellationSize ?? WORLD_CONFIG.CONSTELLATION_SIZE;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Zoom limits for constellation map (no transitions — just clamped)
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 10.0;
  const [zoom, setZoom] = useState(5.0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragButton, setDragButton] = useState<number | null>(null); // Track which button is dragging
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, panX: 0, panY: 0 });
  const [hasDragged, setHasDragged] = useState(false); // Track if mouse moved during drag
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    object: MapObject | null;
    worldX: number;
    worldY: number;
  } | null>(null);
  const [selectedObject, setSelectedObject] = useState<MapObject | null>(null);

  const [playerPos, setPlayerPos] = useState<PlayerPosition>({ x: 0, y: 0, rotation: 0 });
  const [mapObjects, setMapObjects] = useState<MapObject[]>([]);
  const [scannedObjects, setScannedObjects] = useState<MapObject[]>([]); // Objects discovered via radar
  const [constellationName, setConstellationName] = useState('Unknown Constellation');
  const [waypoint, setWaypoint] = useState<{ x: number; y: number; name: string } | null>(null);

  // Initialize from mapData if provided
  useEffect(() => {
    if (mapData) {
      setPlayerPos({
        x: mapData.playerPosition.x,
        y: mapData.playerPosition.y,
        rotation: mapData.playerRotation,
      });
      setConstellationName(mapData.constellationName || 'Unknown Constellation');
      // Don't set waypoint from mapData - we'll get it from waypointUpdate event
      // This prevents stale waypoint data when map is opened via M key

      // Convert map data to MapObject format
      // Note: Planets are NOT shown by default - only discovered via radar scanning.
      // EXCEPTION: Planets where the player has outposts are always visible.
      // Solar systems (suns) are always visible as single icons
      const objects: MapObject[] = [];
      // Planets with player outposts — always visible (orbit updates via planetOrbitalUpdate)
      for (const planet of mapData.planets) {
        if (planet.hasPlayerOutpost) {
          objects.push({
            id: planet.id,
            name: planet.name,
            x: planet.x,
            y: planet.y,
            type: 'planet',
            radius: planet.radius,
            planetType: planet.planetType,
            hasPlayerOutpost: true,
          });
        }
      }
      // Debug: resource zones first (render behind everything as background)
      for (const z of mapData.resourceZones || []) {
        const label =
          z.kind === 'ice'            ? 'Ice Zone' :
          z.kind === 'wreckage'       ? 'Wreckage Zone' :
          z.kind === 'rich_asteroid'  ? 'Rich Field' :
          z.kind === 'nebula'         ? 'Nebula' :
                                        'Asteroid Zone';
        objects.push({
          id: z.id,
          name: label,
          x: z.x,
          y: z.y,
          type: 'resource_zone',
          radius: z.radius,
          starType: z.kind,
        });
      }
      for (const station of mapData.stations) {
        objects.push({
          id: station.id,
          name: station.name,
          x: station.x,
          y: station.y,
          type: 'station',
        });
      }
      // Note: Asteroid zones are now secret and discovered via radar
      // They are no longer shown on the map
      for (const gateway of mapData.gateways || []) {
        objects.push({
          id: gateway.id,
          name: gateway.name,
          x: gateway.x,
          y: gateway.y,
          type: 'gateway',
          targetConstellationName: gateway.targetConstellationName,
        });
      }
      for (const system of mapData.systems || []) {
        objects.push({
          id: system.id,
          name: system.name,
          x: system.x,
          y: system.y,
          type: 'system',
          radius: system.sunRadius,
          starType: system.starType,
        });
      }
      // Tutorial v2: Aura beacons. Drawn with their full proximity radius so
      // the player can see exactly where the modal will trigger.
      for (const beacon of mapData.tutorialBeacons || []) {
        objects.push({
          id: beacon.id,
          name: beacon.name,
          x: beacon.x,
          y: beacon.y,
          type: 'tutorial_beacon',
          radius: beacon.radius,
        });
      }
      for (const ps of mapData.privateStations || []) {
        objects.push({
          id: ps.id,
          name: ps.name,
          x: ps.x,
          y: ps.y,
          type: 'private_station',
          isOwned: ps.isOwned,
        });
      }
      // Harvestables (ice/gas/wreckage) are NOT shown individually on the constellation map.
      // They are represented by their parent resource_zone circles instead.
      // Player-owned drones — always visible, not radar-dependent
      for (const d of mapData.ownedDrones || []) {
        const baseLabel = DRONE_TYPE_DISPLAY[d.droneType as DroneType]?.label ?? 'Drone';
        const level = d.level ?? 1;
        const label = level > 1 ? `${baseLabel} [L${level}]` : baseLabel;
        objects.push({
          id: d.id,
          name: label,
          x: d.x,
          y: d.y,
          type: 'drone',
          isOwned: true,
          droneType: d.droneType,
          droneState: d.state,
        });
      }
      setMapObjects(objects);
    }
  }, [mapData]);

  // No zoom transitions — constellation map is opened via M key, universe via button

  // Subscribe to player position updates and waypoint updates
  useEffect(() => {
    const unsubPlayer = onGameEvent('playerStateUpdate', (state) => {
      setPlayerPos({ x: state.x, y: state.y, rotation: state.rotation });
    });

    // Subscribe to waypoint updates to always show current waypoint
    // This fixes stale waypoint data when map is opened via M key
    const unsubWaypoint = onGameEvent('waypointUpdate', (data) => {
      if (data) {
        setWaypoint({ x: data.x, y: data.y, name: data.name });
      } else {
        setWaypoint(null);
      }
    });

    // Convert any radar entity to a MapObject
    const radarEntityToMapObject = (entity: any): MapObject => {
      // 'hotspot' maps to 'asteroid' on the map
      const mapType = entity.type === 'hotspot' ? 'asteroid' : entity.type;
      return {
        id: entity.id,
        name: entity.name,
        x: entity.x,
        y: entity.y,
        type: mapType,
        radius: entity.radius,
        isScanned: true,
        resourceType: entity.resourceType,
        planetType: entity.planetType,
        starType: entity.starType,
        targetConstellationName: entity.targetConstellationName,
        shipClassId: entity.shipClassId,
        ownerName: entity.ownerName,
      };
    };

    // Constellation map only shows large/notable objects. Small/abundant scan results
    // (individual asteroids, ice, gas, wreckage) are filtered out — they clutter
    // the map without adding navigational value.
    const CONSTELLATION_MAP_EXCLUDED_TYPES = new Set(['asteroid', 'ice_asteroid', 'gas_pocket', 'wreckage']);
    const isShownOnConstellationMap = (obj: MapObject) => !CONSTELLATION_MAP_EXCLUDED_TYPES.has(obj.type);

    // Request current scanned entities when map opens (for entities scanned BEFORE map was opened)
    const unsubInitialScanned = onGameEvent('requestScannedEntitiesResponse', (data) => {
      const newScanned = data.entities.map(radarEntityToMapObject).filter(isShownOnConstellationMap);
      if (newScanned.length > 0) {
        setScannedObjects(newScanned);
      }
    });

    // Emit request for current scanned entities
    emitGameEvent('requestScannedEntities', {});

    // Subscribe to radar scan discoveries - all entity types appear on map
    const unsubRadar = onGameEvent('radarEntitiesDiscovered', (data) => {
      const newScanned = data.entities.map(radarEntityToMapObject).filter(isShownOnConstellationMap);
      if (newScanned.length > 0) {
        setScannedObjects((prev) => {
          const existingIds = new Set(prev.map((o) => o.id));
          const unique = newScanned.filter((o) => !existingIds.has(o.id));
          return [...prev, ...unique];
        });
      }
    });

    // Subscribe to radar clear event - clear scanned objects when scan is reset
    const unsubRadarClear = onGameEvent('radarClearEntities', () => {
      setScannedObjects([]);
    });

    // Subscribe to planet orbital updates for real-time movement on the map
    const unsubOrbital = onGameEvent('planetOrbitalUpdate', (data) => {
      setMapObjects((prev) => {
        const idx = prev.findIndex((o) => o.id === data.id && o.type === 'planet');
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = { ...updated[idx], x: data.x, y: data.y };
        return updated;
      });
      // Also update scanned planets (radar-discovered)
      setScannedObjects((prev) => {
        const idx = prev.findIndex((o) => o.id === data.id && o.type === 'planet');
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = { ...updated[idx], x: data.x, y: data.y };
        return updated;
      });
    });

    return () => {
      unsubPlayer();
      unsubWaypoint();
      unsubInitialScanned();
      unsubRadar();
      unsubRadarClear();
      unsubOrbital();
    };
  }, []);

  const getScale = useCallback((width: number, height: number) => {
    const baseScale = Math.min(width, height) / CONSTELLATION_SIZE;
    return baseScale * zoom;
  }, [zoom, CONSTELLATION_SIZE]);

  // Center on player when mapData loads
  const [hasInitializedPan, setHasInitializedPan] = useState(false);
  useEffect(() => {
    if (containerRef.current && mapData && !hasInitializedPan) {
      const rect = containerRef.current.getBoundingClientRect();
      const scale = getScale(rect.width, rect.height);
      setPan({
        x: -mapData.playerPosition.x * scale,
        y: -mapData.playerPosition.y * scale,
      });
      setHasInitializedPan(true);
    }
  }, [mapData, hasInitializedPan, getScale]);

  // Render canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const scale = getScale(width, height);

    // Clear
    ctx.fillStyle = PALETTE.DARK;
    ctx.fillRect(0, 0, width, height);

    // Helper to convert world to screen coords
    // Game world uses PixiJS coordinates where Y increases downward (screen coords)
    // Canvas also has Y increasing downward, so no inversion needed
    const worldToScreen = (wx: number, wy: number) => ({
      x: centerX + wx * scale + pan.x,
      y: centerY + wy * scale + pan.y,
    });

    // Draw grid
    ctx.strokeStyle = PALETTE.TEAL;
    ctx.globalAlpha = 0.08;
    ctx.lineWidth = 1;

    const gridSpacing = 10000;
    const visibleRangeX = (width / 2) / scale;
    const visibleRangeY = (height / 2) / scale;

    for (let wx = -CONSTELLATION_SIZE; wx <= CONSTELLATION_SIZE; wx += gridSpacing) {
      const start = worldToScreen(wx, -CONSTELLATION_SIZE);
      const end = worldToScreen(wx, CONSTELLATION_SIZE);
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
    }
    for (let wy = -CONSTELLATION_SIZE; wy <= CONSTELLATION_SIZE; wy += gridSpacing) {
      const start = worldToScreen(-CONSTELLATION_SIZE, wy);
      const end = worldToScreen(CONSTELLATION_SIZE, wy);
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
    }

    // Draw constellation boundary
    ctx.globalAlpha = 0.8;
    ctx.strokeStyle = PALETTE.ORANGE;
    ctx.lineWidth = 3;
    const halfSize = CONSTELLATION_SIZE / 2;
    const topLeft = worldToScreen(-halfSize, -halfSize);
    const bottomRight = worldToScreen(halfSize, halfSize);
    ctx.strokeRect(topLeft.x, topLeft.y, bottomRight.x - topLeft.x, bottomRight.y - topLeft.y);

    // Draw origin crosshair
    ctx.globalAlpha = 0.3;
    ctx.strokeStyle = PALETTE.TEAL;
    ctx.lineWidth = 1;
    const origin = worldToScreen(0, 0);
    ctx.beginPath();
    ctx.moveTo(origin.x, 0);
    ctx.lineTo(origin.x, height);
    ctx.moveTo(0, origin.y);
    ctx.lineTo(width, origin.y);
    ctx.stroke();

    // Draw map objects (including scanned objects from radar)
    ctx.globalAlpha = 1;
    const allMapObjects = [...mapObjects, ...scannedObjects];
    for (const obj of allMapObjects) {
      const pos = worldToScreen(obj.x, obj.y);

      if (obj.type === 'asteroid' && obj.radius) {
        // Asteroid field zone (hotspot) — large translucent circle
        ctx.fillStyle = '#4a6a5a';
        ctx.globalAlpha = 0.25;
        const r = Math.max(4, obj.radius * scale);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.4;
        ctx.strokeStyle = '#4a6a5a';
        ctx.stroke();
      } else if (obj.type === 'asteroid') {
        // Individual asteroid — small diamond marker
        const s = Math.max(3, 4 * zoom);
        ctx.globalAlpha = 0.8;
        ctx.fillStyle = '#8aaa7a';
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y - s);
        ctx.lineTo(pos.x + s, pos.y);
        ctx.lineTo(pos.x, pos.y + s);
        ctx.lineTo(pos.x - s, pos.y);
        ctx.closePath();
        ctx.fill();
        ctx.font = '8px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + s + 10);
      } else if (obj.type === 'resource_zone') {
        // Debug: resource zone circles (asteroid/ice/wreckage hotspots)
        const zoneKind = obj.starType || 'asteroid'; // kind stored in starType field
        const color =
          zoneKind === 'ice'           ? '#88ddff' :
          zoneKind === 'wreckage'      ? '#cd7f32' :
          zoneKind === 'rich_asteroid' ? '#ffdd44' :
          zoneKind === 'nebula'        ? '#bb99ff' :
                                         '#4a8a5a';
        const r = Math.max(4, (obj.radius || 8000) * scale);
        // Dashed circle outline
        ctx.globalAlpha = 0.35;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        // Faint fill
        ctx.globalAlpha = 0.08;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.fill();
        // Label
        ctx.globalAlpha = 0.5;
        ctx.fillStyle = color;
        ctx.font = '9px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + r + 10);
      } else if (obj.type === 'planet') {
        let color = PALETTE.ORANGE;
        if (obj.planetType === 'terran') color = '#44aa66';
        else if (obj.planetType === 'ice') color = '#88ccff';
        else if (obj.planetType === 'gas') color = '#ddaa44';
        else if (obj.planetType === 'volcanic') color = '#ff4422';
        else if (obj.planetType === 'barren') color = '#aa8866';
        else if (obj.planetType === 'rocky') color = '#888888';

        // Filled circle, smaller than the sun
        const r = Math.max(3, Math.min(8, (obj.radius || 600) * scale * 0.8));
        ctx.globalAlpha = 0.85;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.fill();

        // Outpost indicator: green ring around planets with player outposts
        if (obj.hasPlayerOutpost) {
          ctx.globalAlpha = 0.8;
          ctx.strokeStyle = '#4ade80';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, r + 3, 0, Math.PI * 2);
          ctx.stroke();
        }

        // Name
        ctx.globalAlpha = 0.7;
        ctx.fillStyle = color;
        ctx.font = '9px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + r + 10);
      } else if (obj.type === 'tutorial_beacon') {
        // Tutorial v2: single ring at proximity radius + single center dot.
        const ringR = (obj.radius ?? 750) * scale;

        ctx.globalAlpha = 0.4;
        ctx.strokeStyle = '#20a5a6';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, ringR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.globalAlpha = 1;
        ctx.fillStyle = '#20a5a6';
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, Math.max(3, 4 * zoom), 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = 0.95;
        ctx.fillStyle = '#20a5a6';
        ctx.font = '10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y - Math.max(6, 8 * zoom) - 4);
      } else if (obj.type === 'station') {
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = '#ffff44';
        const size = Math.max(3, Math.min(8, 3 * zoom));
        ctx.fillRect(pos.x - size, pos.y - size, size * 2, size * 2);

        ctx.font = '10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + size + 12);
      } else if (obj.type === 'private_station') {
        // Private stations: green diamond if yours, cyan diamond if someone else's
        const color = obj.isOwned ? '#4ade80' : '#22d3ee';
        const size = Math.max(3, Math.min(8, 3 * zoom));

        // Diamond shape
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y - size);
        ctx.lineTo(pos.x + size, pos.y);
        ctx.lineTo(pos.x, pos.y + size);
        ctx.lineTo(pos.x - size, pos.y);
        ctx.closePath();
        ctx.fill();

        // Border
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, size + 2, 0, Math.PI * 2);
        ctx.stroke();

        // Name
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = color;
        ctx.font = '10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + size + 10);
      } else if (obj.type === 'gateway') {
        // Draw gateway as cyan/teal portal ring
        const gatewaySize = Math.max(6, 10 * zoom);

        // Outer ring
        ctx.globalAlpha = 0.9;
        ctx.strokeStyle = '#00dddd';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, gatewaySize, 0, Math.PI * 2);
        ctx.stroke();

        // Inner glow
        ctx.globalAlpha = 0.4;
        ctx.fillStyle = '#00dddd';
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, gatewaySize * 0.6, 0, Math.PI * 2);
        ctx.fill();

        // Center dot
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#00ffff';
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 2, 0, Math.PI * 2);
        ctx.fill();

        // Name label
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = '#00dddd';
        ctx.font = '10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + gatewaySize + 12);
      } else if (obj.type === 'system') {
        // Draw solar system as a single icon (sun with orbit ring indicator)
        // Color based on star type
        let systemColor = '#ffcc00'; // yellow default
        if (obj.starType === 'red_dwarf') systemColor = '#ff6644';
        else if (obj.starType === 'blue_giant') systemColor = '#4488ff';
        else if (obj.starType === 'neutron') systemColor = '#aaaaff';

        const r = Math.max(10, Math.min(30, (obj.radius || 600) * scale * 2));

        // Filled circle — star type color only
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = systemColor;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.fill();

        // [SCAN] indicator for scanned objects
        if (obj.isScanned) {
          ctx.globalAlpha = 0.8;
          ctx.fillStyle = '#22c55e';
          ctx.font = '8px monospace';
          ctx.fillText('[SCAN]', pos.x, pos.y - r - 4);
        }

        // Name
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = systemColor;
        ctx.font = '10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + r + 12);
      } else if (obj.type === 'sun') {
        let sunColor = '#ffcc00';
        if (obj.starType === 'red_dwarf') sunColor = '#ff6644';
        else if (obj.starType === 'blue_giant') sunColor = '#4488ff';
        else if (obj.starType === 'neutron') sunColor = '#aaaaff';
        const r = Math.max(8, Math.min(20, (obj.radius || 300) * scale * 2));
        ctx.globalAlpha = 0.7;
        ctx.fillStyle = sunColor;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = sunColor;
        ctx.font = '10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + r + 12);
      } else if (obj.type === 'player') {
        const s = Math.max(4, 5 * zoom);
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = PALETTE.TEAL;
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y - s);
        ctx.lineTo(pos.x + s * 0.7, pos.y + s * 0.5);
        ctx.lineTo(pos.x - s * 0.7, pos.y + s * 0.5);
        ctx.closePath();
        ctx.fill();
        ctx.font = '9px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + s + 10);
      } else if (obj.type === 'nebula') {
        const r = Math.max(6, (obj.radius || 5000) * scale);
        ctx.globalAlpha = 0.15;
        ctx.fillStyle = '#ff66cc';
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.5;
        ctx.strokeStyle = '#ff66cc';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = '#ff66cc';
        ctx.font = '9px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + r + 10);
      } else if (obj.type === 'drone') {
        const r = Math.max(2, Math.min(5, 2 * zoom));
        if (obj.isOwned) {
          // Player-owned drone — bright green with pulsing outline
          ctx.globalAlpha = 0.9;
          const color = hexToCss(DRONE_TYPE_DISPLAY[obj.droneType as DroneType]?.mapColor ?? 0xffffff);
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
          ctx.fill();
          // Outer ring
          ctx.strokeStyle = color;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, r + 2, 0, Math.PI * 2);
          ctx.stroke();
          // Label
          ctx.font = '8px monospace';
          ctx.textAlign = 'center';
          ctx.fillText(obj.name, pos.x, pos.y + r + 10);
        } else {
          // Radar-scanned drone (not owned)
          ctx.globalAlpha = 0.7;
          ctx.fillStyle = '#66ccff';
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (obj.type === 'drop') {
        const s = Math.max(3, Math.min(6, 4 * zoom));
        ctx.globalAlpha = 0.8;
        ctx.fillStyle = '#ffdd44';
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y - s);
        ctx.lineTo(pos.x + s, pos.y);
        ctx.lineTo(pos.x, pos.y + s);
        ctx.lineTo(pos.x - s, pos.y);
        ctx.closePath();
        ctx.fill();
        ctx.font = '9px monospace';
        ctx.textAlign = 'center';
        ctx.fillText('Loot', pos.x, pos.y + s + 10);
      } else if (obj.type === 'settlement') {
        // Rogue settlement — violet core ring (matches in-flight visual: 0x9b5cff)
        const r = Math.max(4, Math.min(8, 4 * zoom));
        ctx.globalAlpha = 0.85;
        ctx.fillStyle = '#9b5cff';
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.6;
        ctx.strokeStyle = '#9b5cff';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r + 3, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = '#9b5cff';
        ctx.font = '9px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + r + 12);
      } else if (obj.type === 'beacon') {
        const r = Math.max(4, (obj.radius || 500) * scale);
        ctx.globalAlpha = 0.2;
        ctx.fillStyle = '#ff4466';
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.7;
        ctx.strokeStyle = '#ff4466';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = '#ff4466';
        ctx.font = '9px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(obj.name, pos.x, pos.y + r + 10);
      }

      // Generic [SCAN] tag for all radar-discovered entities
      if (obj.isScanned) {
        ctx.globalAlpha = 0.7;
        ctx.fillStyle = '#22c55e';
        ctx.font = '8px monospace';
        ctx.textAlign = 'center';
        ctx.fillText('[SCAN]', pos.x, pos.y - 10);
      }
    }

    // Draw waypoint if set - small diamond marker
    if (waypoint) {
      const waypointScreen = worldToScreen(waypoint.x, waypoint.y);
      const size = 5; // Small fixed size

      // Draw small diamond marker
      ctx.globalAlpha = 1;
      ctx.fillStyle = PALETTE.ORANGE;
      ctx.beginPath();
      ctx.moveTo(waypointScreen.x, waypointScreen.y - size);
      ctx.lineTo(waypointScreen.x + size, waypointScreen.y);
      ctx.lineTo(waypointScreen.x, waypointScreen.y + size);
      ctx.lineTo(waypointScreen.x - size, waypointScreen.y);
      ctx.closePath();
      ctx.fill();

      // Small glow ring
      ctx.globalAlpha = 0.5;
      ctx.strokeStyle = PALETTE.ORANGE;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(waypointScreen.x, waypointScreen.y, size + 3, 0, Math.PI * 2);
      ctx.stroke();

      // Floating coordinates label above the waypoint
      ctx.globalAlpha = 1;
      ctx.font = '10px monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = PALETTE.ORANGE;
      ctx.fillText(
        `(${Math.round(waypoint.x)}, ${Math.round(waypoint.y)})`,
        waypointScreen.x,
        waypointScreen.y - size - 6
      );
    }

    // Draw player
    ctx.globalAlpha = 1;
    const playerScreen = worldToScreen(playerPos.x, playerPos.y);
    const rotRad = (playerPos.rotation - 90) * (Math.PI / 180);
    const shipSize = Math.max(5, Math.min(10, 6 * zoom));

    ctx.fillStyle = PALETTE.TEAL;
    ctx.beginPath();
    ctx.moveTo(
      playerScreen.x + Math.cos(rotRad) * shipSize,
      playerScreen.y + Math.sin(rotRad) * shipSize
    );
    ctx.lineTo(
      playerScreen.x + Math.cos(rotRad + 2.4) * shipSize * 0.8,
      playerScreen.y + Math.sin(rotRad + 2.4) * shipSize * 0.8
    );
    ctx.lineTo(
      playerScreen.x + Math.cos(rotRad - 2.4) * shipSize * 0.8,
      playerScreen.y + Math.sin(rotRad - 2.4) * shipSize * 0.8
    );
    ctx.closePath();
    ctx.fill();

    // Glow ring
    ctx.strokeStyle = PALETTE.TEAL;
    ctx.globalAlpha = 0.4;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(playerScreen.x, playerScreen.y, shipSize * 1.5, 0, Math.PI * 2);
    ctx.stroke();

  }, [zoom, pan, playerPos, mapObjects, scannedObjects, waypoint, getScale]);

  // Handle wheel zoom - will be attached manually to avoid passive listener warning
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.4 : 0.4;

    setZoom((prev) => {
      const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + delta));
      const ratio = clampedZoom / prev;
      setPan((p) => ({ x: p.x * ratio, y: p.y * ratio }));
      return clampedZoom;
    });
  }, [MIN_ZOOM, MAX_ZOOM]);

  // Manually attach wheel event listener to avoid passive listener warning
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Helper to convert screen coords to world coords
  // Game world uses PixiJS coordinates where Y increases downward (screen coords)
  // Canvas also has Y increasing downward, so no inversion needed
  const screenToWorld = useCallback((screenX: number, screenY: number) => {
    if (!containerRef.current) return { x: 0, y: 0 };
    const rect = containerRef.current.getBoundingClientRect();
    const canvasX = screenX - rect.left;
    const canvasY = screenY - rect.top;
    const width = rect.width;
    const height = rect.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const scale = getScale(width, height);
    return {
      x: (canvasX - centerX - pan.x) / scale,
      y: (canvasY - centerY - pan.y) / scale,
    };
  }, [pan, getScale]);

  // Helper to find object at world position
  const findObjectAtPosition = useCallback((worldX: number, worldY: number): MapObject | null => {
    const allObjs = [...mapObjects, ...scannedObjects];

    // Check gateways first (small hitbox, high priority)
    for (const obj of allObjs) {
      if (obj.type === 'gateway') {
        const dist = Math.sqrt((obj.x - worldX) ** 2 + (obj.y - worldY) ** 2);
        if (dist < 2000) return obj;
      }
    }
    // Check stations (smallest hitbox)
    for (const obj of allObjs) {
      if (obj.type === 'station' || obj.type === 'private_station') {
        const dist = Math.sqrt((obj.x - worldX) ** 2 + (obj.y - worldY) ** 2);
        if (dist < 2000) return obj;
      }
    }
    // Then systems (solar systems shown as single icon)
    for (const obj of allObjs) {
      if (obj.type === 'system') {
        const dist = Math.sqrt((obj.x - worldX) ** 2 + (obj.y - worldY) ** 2);
        const hitRadius = (obj.radius || 600) * 3;
        if (dist < hitRadius) return obj;
      }
    }
    // Then planets
    for (const obj of allObjs) {
      if (obj.type === 'planet') {
        const dist = Math.sqrt((obj.x - worldX) ** 2 + (obj.y - worldY) ** 2);
        const hitRadius = (obj.radius || 600) * 0.8 + 500;
        if (dist < hitRadius) return obj;
      }
    }
    // Individual asteroids (small markers)
    for (const obj of allObjs) {
      if (obj.type === 'asteroid' && !obj.radius) {
        const dist = Math.sqrt((obj.x - worldX) ** 2 + (obj.y - worldY) ** 2);
        if (dist < 3000) return obj;
      }
    }
    // Asteroid fields / hotspot zones (largest)
    for (const obj of allObjs) {
      if (obj.type === 'asteroid' && obj.radius) {
        const dist = Math.sqrt((obj.x - worldX) ** 2 + (obj.y - worldY) ** 2);
        if (dist < obj.radius) return obj;
      }
    }
    // Small scanned entities (players, drones, drops, beacons, ice/gas/wreckage)
    for (const obj of allObjs) {
      if (['player', 'drone', 'drop', 'beacon', 'sun', 'ice_asteroid', 'gas_pocket', 'wreckage'].includes(obj.type)) {
        const dist = Math.sqrt((obj.x - worldX) ** 2 + (obj.y - worldY) ** 2);
        if (dist < 3000) return obj;
      }
    }
    // Nebulae (large radius)
    for (const obj of allObjs) {
      if (obj.type === 'nebula') {
        const dist = Math.sqrt((obj.x - worldX) ** 2 + (obj.y - worldY) ** 2);
        if (dist < (obj.radius || 5000)) return obj;
      }
    }
    // Resource zones (debug — largest, lowest priority)
    for (const obj of allObjs) {
      if (obj.type === 'resource_zone') {
        const dist = Math.sqrt((obj.x - worldX) ** 2 + (obj.y - worldY) ** 2);
        if (dist < (obj.radius || 8000)) return obj;
      }
    }
    return null;
  }, [mapObjects, scannedObjects]);

  // Handle LEFT click - set waypoint (action)
  const handleLeftClick = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const world = screenToWorld(e.clientX, e.clientY);

    const newWaypoint = {
      x: world.x,
      y: world.y,
      name: `Waypoint (${Math.round(world.x)}, ${Math.round(world.y)})`,
    };

    emitGameEvent('setWaypoint', newWaypoint);
    setWaypoint(newWaypoint);
  }, [screenToWorld]);

  // Handle RIGHT click - select object and show tooltip
  const handleRightClick = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;

    const world = screenToWorld(e.clientX, e.clientY);
    const obj = findObjectAtPosition(world.x, world.y);

    setSelectedObject(obj);
    setTooltip({
      x: e.clientX - containerRef.current.getBoundingClientRect().left,
      y: e.clientY - containerRef.current.getBoundingClientRect().top,
      object: obj,
      worldX: world.x,
      worldY: world.y,
    });
  }, [screenToWorld, findObjectAtPosition]);

  // Handle drag (both LEFT and RIGHT mouse buttons for panning)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Both left (0) and right (2) buttons can pan
    if (e.button === 0 || e.button === 2) {
      setIsDragging(true);
      setDragButton(e.button);
      setHasDragged(false);
      setDragStart({ x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y });
      // Clear tooltip when starting a potential drag
      setTooltip(null);
    }
  }, [pan]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Only start panning after moving more than 5px (to distinguish from clicks)
      if (distance > 5) {
        setHasDragged(true);
        setPan({ x: dragStart.panX + dx, y: dragStart.panY + dy });
      }
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (isDragging && (e.button === 0 || e.button === 2)) {
      // Check if this was a click (not a drag) - less than 5px movement
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < 5) {
        if (e.button === 0) {
          // Left click without drag - set waypoint
          handleLeftClick(e);
        } else if (e.button === 2) {
          // Right click without drag - show tooltip
          handleRightClick(e);
        }
      }
    }
    setIsDragging(false);
    setDragButton(null);
    setHasDragged(false);
  }, [isDragging, dragStart, handleLeftClick, handleRightClick]);

  // Prevent context menu from showing (we handle right-click ourselves)
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
    setDragButton(null);
    setHasDragged(false);
    setTooltip(null);
  }, []);

  // Handle keyboard
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'm' || e.key === 'M') {
        onClose();
      }
      // X key clears waypoint
      if ((e.key === 'x' || e.key === 'X') && waypoint) {
        emitGameEvent('clearWaypoint');
        setWaypoint(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, waypoint]);

  return (
    <div style={overlayStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>{constellationName.toUpperCase()}</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={closeButtonStyle} onClick={onZoomToUniverse}>
            Universe Map
          </button>
          <button style={closeButtonStyle} onClick={onClose}>
            Close (M/ESC)
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        style={canvasContainerStyle}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
      >
        <canvas ref={canvasRef} style={{ display: 'block' }} />

        {tooltip && (
          <div style={{
            ...tooltipStyle,
            left: Math.min(tooltip.x + 15, (containerRef.current?.clientWidth || 800) - 200),
            top: Math.min(tooltip.y - 10, (containerRef.current?.clientHeight || 600) - 150),
          }}>
            {tooltip.object ? (
              <>
                <div style={tooltipHeaderStyle}>
                  <span style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: (tooltip.object.type === 'station' || tooltip.object.type === 'private_station') ? '2px' : '50%',
                    backgroundColor:
                      tooltip.object.type === 'station' ? '#ffff44' :
                      tooltip.object.type === 'private_station' ? (tooltip.object.isOwned ? '#4ade80' : '#22d3ee') :
                      tooltip.object.type === 'planet' ? PALETTE.ORANGE :
                      tooltip.object.type === 'gateway' ? '#00dddd' :
                      tooltip.object.type === 'system' || tooltip.object.type === 'sun' ? '#ffcc00' :
                      tooltip.object.type === 'player' ? PALETTE.TEAL :
                      tooltip.object.type === 'nebula' ? '#ff66cc' :
                      tooltip.object.type === 'drone' ? (tooltip.object.isOwned ? '#44ff88' : '#66ccff') :
                      tooltip.object.type === 'drop' ? '#ffdd44' :
                      tooltip.object.type === 'beacon' ? '#ff4466' :
                      tooltip.object.type === 'ice_asteroid' ? '#88ddff' :
                      tooltip.object.type === 'gas_pocket' ? '#bb99ff' :
                      tooltip.object.type === 'wreckage' ? '#cd7f32' :
                      tooltip.object.type === 'resource_zone' ? (
                        tooltip.object.starType === 'ice' ? '#88ddff' :
                        tooltip.object.starType === 'wreckage' ? '#cd7f32' :
                        tooltip.object.starType === 'rich_asteroid' ? '#ffdd44' :
                        tooltip.object.starType === 'nebula' ? '#bb99ff' : '#4a8a5a'
                      ) :
                      '#4a6a5a',
                    display: 'inline-block',
                  }} />
                  <span style={{ color: PALETTE.TEAL }}>{tooltip.object.name}</span>
                  {tooltip.object.isScanned && (
                    <span style={{
                      marginLeft: '6px',
                      color: '#22c55e',
                      fontSize: '9px',
                      fontWeight: 600,
                      padding: '1px 4px',
                      backgroundColor: 'rgba(34, 197, 94, 0.2)',
                      borderRadius: '2px',
                    }}>
                      SCANNED
                    </span>
                  )}
                </div>
                <div style={tooltipRowStyle}>
                  <span style={tooltipLabelStyle}>Type</span>
                  <span style={tooltipValueStyle}>
                    {tooltip.object.type === 'station' ? 'Station' :
                     tooltip.object.type === 'private_station' ? (tooltip.object.isOwned ? 'Your Outpost' : 'Private Outpost') :
                     tooltip.object.type === 'planet' ? `Planet (${tooltip.object.planetType || 'rocky'})` :
                     tooltip.object.type === 'gateway' ? 'Gateway' :
                     tooltip.object.type === 'system' ? `Solar System (${tooltip.object.starType || 'yellow'})` :
                     tooltip.object.type === 'sun' ? `Star (${tooltip.object.starType || 'yellow'})` :
                     tooltip.object.type === 'player' ? 'Player' :
                     tooltip.object.type === 'nebula' ? 'Nebula' :
                     tooltip.object.type === 'drone' ? (tooltip.object.isOwned ? `Your ${tooltip.object.droneType || 'Drone'} (${tooltip.object.droneState || 'idle'})` : 'Drone') :
                     tooltip.object.type === 'drop' ? 'Loot Container' :
                     tooltip.object.type === 'beacon' ? 'Interdiction Beacon' :
                     tooltip.object.type === 'ice_asteroid' ? 'Ice Asteroid' :
                     tooltip.object.type === 'gas_pocket' ? 'Gas Pocket' :
                     tooltip.object.type === 'wreckage' ? 'Wreckage' :
                     tooltip.object.type === 'resource_zone' ? tooltip.object.name :
                     tooltip.object.type === 'asteroid' && !tooltip.object.radius ? 'Asteroid' :
                     'Asteroid Field'}
                  </span>
                </div>
                {tooltip.object.type === 'gateway' && tooltip.object.targetConstellationName && (
                  <div style={tooltipRowStyle}>
                    <span style={tooltipLabelStyle}>Destination</span>
                    <span style={{ ...tooltipValueStyle, color: '#00dddd' }}>
                      {tooltip.object.targetConstellationName}
                    </span>
                  </div>
                )}
                <div style={tooltipRowStyle}>
                  <span style={tooltipLabelStyle}>Position</span>
                  <span style={tooltipValueStyle}>
                    {Math.round(tooltip.object.x).toLocaleString()}, {Math.round(tooltip.object.y).toLocaleString()}
                  </span>
                </div>
                {tooltip.object.radius && (
                  <div style={tooltipRowStyle}>
                    <span style={tooltipLabelStyle}>
                      {(tooltip.object.type === 'asteroid' || tooltip.object.type === 'resource_zone') ? 'Zone Radius' : 'Size'}
                    </span>
                    <span style={tooltipValueStyle}>
                      {Math.round(tooltip.object.radius).toLocaleString()}m
                    </span>
                  </div>
                )}
                <div style={tooltipRowStyle}>
                  <span style={tooltipLabelStyle}>Distance</span>
                  <span style={tooltipValueStyle}>
                    {Math.round(Math.sqrt(
                      (tooltip.object.x - playerPos.x) ** 2 +
                      (tooltip.object.y - playerPos.y) ** 2
                    )).toLocaleString()}m
                  </span>
                </div>
                <div style={{
                  marginTop: '10px',
                  paddingTop: '8px',
                  borderTop: `1px solid ${PALETTE.DIM}`,
                  fontSize: '10px',
                  color: PALETTE.DIM,
                  textAlign: 'center',
                }}>
                  Left-click to set waypoint
                </div>
              </>
            ) : (
              <>
                <div style={tooltipHeaderStyle}>
                  <span style={{ color: PALETTE.DIM }}>Empty Space</span>
                </div>
                <div style={tooltipRowStyle}>
                  <span style={tooltipLabelStyle}>Position</span>
                  <span style={tooltipValueStyle}>
                    {Math.round(tooltip.worldX).toLocaleString()}, {Math.round(tooltip.worldY).toLocaleString()}
                  </span>
                </div>
                <div style={tooltipRowStyle}>
                  <span style={tooltipLabelStyle}>Distance</span>
                  <span style={tooltipValueStyle}>
                    {Math.round(Math.sqrt(
                      (tooltip.worldX - playerPos.x) ** 2 +
                      (tooltip.worldY - playerPos.y) ** 2
                    )).toLocaleString()}m
                  </span>
                </div>
                <div style={{
                  marginTop: '10px',
                  paddingTop: '8px',
                  borderTop: `1px solid ${PALETTE.DIM}`,
                  fontSize: '10px',
                  color: PALETTE.DIM,
                  textAlign: 'center',
                }}>
                  Left-click to set waypoint
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div style={instructionsStyle}>
        Left-click to set waypoint | Right-click to inspect | Drag to pan | X to clear waypoint | Scroll to zoom | M/ESC to close
      </div>
    </div>
  );
}

export default ConstellationMapOverlay;
